import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { listMantenimientoDetalles } from '@/features/mantenimiento/api';

/**
 * "Ver Artículos" — read-only popup on the Despacho stage card. Same
 * data + visual layout as the mantenimiento `NumerosSerieDespachoDialog`,
 * minus the inline edit affordance: vendors should be able to consult
 * the serial / dispatch numbers from the venta detail without entering
 * the maintenance flow.
 */
export function DespachoArticulosDialog({
  open,
  onClose,
  ventaId,
}: {
  open: boolean;
  onClose: () => void;
  ventaId: string;
}) {
  const detallesQ = useQuery({
    queryKey: ['mantenimiento', 'detalles', ventaId],
    queryFn: () => listMantenimientoDetalles(ventaId),
    enabled: open && Boolean(ventaId),
    staleTime: 30_000,
  });
  const rows = useMemo(() => detallesQ.data ?? [], [detallesQ.data]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Números de Serie / Números de Despacho</DialogTitle>
          <DialogDescription>Venta Nº {ventaId}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {detallesQ.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : detallesQ.isError ? (
            <div className="py-6 text-center text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Sin líneas activas.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <Th>Artículo</Th>
                    <Th>Num. de Serie</Th>
                    <Th>Num. de Despacho</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <Td className="font-semibold text-foreground">
                        {row.articulo}
                      </Td>
                      <Td className={cn(row.nroSerie ? '' : 'text-muted-foreground')}>
                        {row.nroSerie || 'Pendiente'}
                      </Td>
                      <Td
                        className={cn(row.nroDespacho ? '' : 'text-muted-foreground')}
                      >
                        {row.nroDespacho || 'Pendiente'}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            className="w-full bg-foreground text-background hover:bg-foreground/90"
            onClick={onClose}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <th className={cn('px-3 py-2 font-semibold', className)}>{children}</th>;
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn('px-3 py-2 align-middle', className)}>{children}</td>;
}
